import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import { useProfileSections } from '../hooks/useProfileSections';
import { Divider } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  FC,
  RefObject,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { Events } from 'react-scroll';
import NavItem from './NavItem';
import {
  focusNavigationElement,
  getCurrentActiveTab,
  getNavContentClass,
  getProfileNavbarClassName,
  updateWindowHistory,
} from './navBarUtils';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';

export const NAVBAR_HEIGHT_OFFSET = -130;

export const PROFILE_NAVBAR_BI_TAB_TYPE = 'top tab';

export type ProfileNavbarSection =
  (typeof PROFILE_PAGE_SECTIONS)[keyof typeof PROFILE_PAGE_SECTIONS];

type Props = {
  isMobileView?: boolean;
  navigationRefs: RefObject<Record<string, HTMLElement>>;
  showBack?: boolean;
  onTabChange?: (tab: string) => void;
  activeTab?: string;
  isMiniContactPanelVisible?: boolean;
};

const NavBar: FC<Props> = ({
  isMobileView,
  showBack,
  onTabChange,
  activeTab,
  navigationRefs,
  isMiniContactPanelVisible,
}) => {
  const { t } = useTranslation();
  const { logProfilePageClick } = useProfilePageBi();
  const { isRenderedInModal } = useProfileContext();
  const {
    state: { numberOfResults: numberOfTemplates, visibleTemplatesCount },
  } = useTemplatesContext();

  const {
    shouldShowClientProjects,
    shouldShowServicesAndReviews,
    shouldShowTemplates,
    getValidActiveTab,
  } = useProfileSections(numberOfTemplates);

  const [navItemWasClicked, setNavItemWasClicked] = useState(
    getValidActiveTab(activeTab),
  );
  const [isClickScrolling, setIsClickScrolling] = useState(false);

  const registerScrollEnds = useCallback(
    (section: string) => {
      setIsClickScrolling(true);
      Events.scrollEvent.register('end', function (_to: any, _element: any) {
        setNavItemWasClicked(section);
        onTabChange?.(section);
        setIsClickScrolling(false);
        updateWindowHistory(section);
        Events.scrollEvent.remove('end');
      });
    },
    [onTabChange],
  );

  const handleSetActiveTab = useCallback(
    (section: string) => {
      if (!isClickScrolling) {
        setNavItemWasClicked(section);
      }
    },
    [isClickScrolling],
  );

  const handleMobileTabClick = useCallback(
    (section: string, biName?: string, scrollIntoView?: () => void) => {
      setNavItemWasClicked(section);
      onTabChange?.(section);
      logProfilePageClick(PROFILE_NAVBAR_BI_TAB_TYPE, biName);
      updateWindowHistory(section);
      scrollIntoView?.();
    },
    [onTabChange, logProfilePageClick],
  );

  const handleDesktopTabClick = useCallback(
    (section: string, biName?: string) => {
      registerScrollEnds(section);
      setNavItemWasClicked(section);
      onTabChange?.(section);
      logProfilePageClick(PROFILE_NAVBAR_BI_TAB_TYPE, biName);
      focusNavigationElement(navigationRefs, section);
    },
    [registerScrollEnds, onTabChange, logProfilePageClick, navigationRefs],
  );

  const profileNavbarClassName = useMemo(
    () =>
      getProfileNavbarClassName(
        isMobileView,
        isRenderedInModal,
        showBack,
        isMiniContactPanelVisible,
      ),
    [isMobileView, isRenderedInModal, showBack, isMiniContactPanelVisible],
  );

  const navContentClass = useMemo(
    () => getNavContentClass(isMobileView),
    [isMobileView],
  );

  const currentActiveTab = useMemo(
    () => getCurrentActiveTab(isMobileView, activeTab, navItemWasClicked),
    [isMobileView, activeTab, navItemWasClicked],
  );

  const createActiveTabHandler = useCallback(
    (section: string) => () => handleSetActiveTab(section),
    [handleSetActiveTab],
  );

  return (
    <div className={profileNavbarClassName} data-hook="profile-navbar">
      <nav
        aria-label={t('profile-page-navigation-tabs-aria-label')}
        className={navContentClass}
      >
        <ul style={{ margin: '12px 0' }}>
          {shouldShowClientProjects && (
            <NavItem
              section={PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS}
              textKey="profile-page-nav-bar-client-projects"
              biName="client projects tab"
              isMobileView={isMobileView}
              isActive={
                currentActiveTab === PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS
              }
              onMobileClick={handleMobileTabClick}
              onDesktopClick={handleDesktopTabClick}
              offset={NAVBAR_HEIGHT_OFFSET}
              onSetActive={createActiveTabHandler(
                PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS,
              )}
              hashSpy={!isClickScrolling}
            />
          )}
          <NavItem
            section={PROFILE_PAGE_SECTIONS.ABOUT}
            textKey="profile-page-nav-bar-about"
            biName="about tab"
            dataHook="navbar-about-tab"
            isMobileView={isMobileView}
            isActive={currentActiveTab === PROFILE_PAGE_SECTIONS.ABOUT}
            onMobileClick={handleMobileTabClick}
            onDesktopClick={handleDesktopTabClick}
            offset={NAVBAR_HEIGHT_OFFSET}
            onSetActive={createActiveTabHandler(PROFILE_PAGE_SECTIONS.ABOUT)}
            hashSpy={!isClickScrolling}
          />
          {shouldShowServicesAndReviews && (
            <NavItem
              section={PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS}
              textKey="profile-page-nav-bar-services-and-reviews"
              biName="services and reviews tab"
              isMobileView={isMobileView}
              isActive={
                currentActiveTab === PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS
              }
              onMobileClick={handleMobileTabClick}
              onDesktopClick={handleDesktopTabClick}
              offset={NAVBAR_HEIGHT_OFFSET}
              onSetActive={createActiveTabHandler(
                PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS,
              )}
              hashSpy={!isClickScrolling}
            />
          )}
          {shouldShowTemplates && (
            <NavItem
              section={PROFILE_PAGE_SECTIONS.TEMPLATES}
              textKey="profile-page-nav-bar-templates"
              biName="templates tab"
              isMobileView={isMobileView}
              isActive={currentActiveTab === PROFILE_PAGE_SECTIONS.TEMPLATES}
              count={isMobileView ? visibleTemplatesCount : undefined}
              onMobileClick={handleMobileTabClick}
              onDesktopClick={handleDesktopTabClick}
              offset={NAVBAR_HEIGHT_OFFSET}
              onSetActive={createActiveTabHandler(
                PROFILE_PAGE_SECTIONS.TEMPLATES,
              )}
              hashSpy={!isClickScrolling}
            />
          )}
        </ul>
      </nav>
      {!isMobileView && <Divider />}
    </div>
  );
};

export default NavBar;
